'use client';

import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
import type React from 'react';

type LocalizedHref = React.ComponentProps<typeof Link>['href'];
type AppButtonVariant = 'ghost' | 'outline' | 'primary' | 'unstyled';

interface ButtonProps {
    innerText?: string;
    variant?: AppButtonVariant;
    customClassName?: string;
    children?: React.ReactNode;
    href?: LocalizedHref;
    externalUrl?: string;
    loading?: boolean;
    type?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
    onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
}

export default function AppButton({
    innerText = '',
    variant = 'ghost',
    customClassName,
    children,
    href,
    loading,
    externalUrl,
    type,
    disabled,
    onClick,
}: ButtonProps) {
    const className = cn(
        variant !== 'unstyled' &&
            'text-content-on-dark text-center w-fit px-7 py-4 font-bold rounded-[12px] border-content-on-dark transition duration-300',
        variant === 'ghost' &&
            'hover:bg-brand-primary-hover hover:border-brand-primary-hover disabled:hover:bg-transparent',
        variant === 'outline' && 'border hover:bg-brand-primary-hover hover:border-brand-primary-hover',
        variant === 'primary' &&
            'border border-brand-primary bg-brand-primary hover:bg-brand-primary-hover hover:border-brand-primary-hover disabled:bg-brand-disabled disabled:border-brand-disabled disabled:hover:bg-brand-disabled disabled:hover:border-brand-disabled',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        customClassName,
    );

    if (externalUrl) {
        return (
            <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
                onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
            >
                {innerText}
                {children}
            </a>
        );
    }

    return href ? (
        <Link href={href} className={className} onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}>
            {loading ? (
                <div className="spinner" />
            ) : (
                <>
                    {innerText}
                    {children}
                </>
            )}
        </Link>
    ) : (
        <button
            type={type}
            disabled={disabled}
            className={className}
            onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
        >
            {loading ? (
                <div className="spinner" />
            ) : (
                <>
                    {innerText}
                    {children}
                </>
            )}
        </button>
    );
}
